/**
 * The icon set.
 *
 * Real inline SVG, 16px, `fill="none" stroke="currentColor" stroke-width="1.5"`. There is no
 * icon font and no sprite sheet: an icon that ships as two letters is a placeholder, and this
 * file is what replaces it.
 *
 * Every glyph is decorative. It carries `aria-hidden` and inherits colour, so the accessible
 * name always comes from the label beside it. Nothing here is ever the only signal for a
 * status: the word is rendered too.
 */
import type { ReactElement, SVGProps } from 'react';

export type IconName =
  | 'chevron'
  | 'search'
  | 'download'
  | 'star'
  | 'edit'
  | 'warning'
  | 'external'
  | 'eye'
  | 'eyeOff'
  | 'home'
  | 'orders'
  | 'stock'
  | 'customers'
  | 'support'
  | 'finance'
  | 'growth'
  | 'settings'
  | 'bench';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** Rotation in degrees. The chevron points right at 0, down at 90. */
  turn?: number;
}

function svgProps({ turn, style, className, ...rest }: IconProps): SVGProps<SVGSVGElement> {
  return {
    width: 16,
    height: 16,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    focusable: 'false',
    'aria-hidden': true,
    className: className ? `o-icon ${className}` : 'o-icon',
    style: turn ? { transform: `rotate(${turn}deg)`, ...style } : style,
    ...rest,
  };
}

export function ChevronIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M6 3.5 10.5 8 6 12.5" />
    </svg>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.2 10.2 13.5 13.5" />
    </svg>
  );
}

export function DownloadIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M8 2v7.5" />
      <path d="M5 7 8 10l3-3" />
      <path d="M2.75 11.5v1.25a.75.75 0 0 0 .75.75h9a.75.75 0 0 0 .75-.75V11.5" />
    </svg>
  );
}

export function StarIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M8 2.25 9.85 6l4.15.6-3 2.93.71 4.13L8 11.71l-3.71 1.95L5 9.53 2 6.6 6.15 6z" />
    </svg>
  );
}

export function EditIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M11.2 2.55a1.2 1.2 0 0 1 1.7 1.7L5.6 11.55l-2.35.6.6-2.35z" />
      <path d="M2.75 14h10.5" />
    </svg>
  );
}

export function WarningIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M8 2.25 14.5 13.5h-13z" />
      <path d="M8 6.5v3.25" />
      <path d="M8 11.6h.01" />
    </svg>
  );
}

export function ExternalIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M9.5 2.5h4v4" />
      <path d="M13.5 2.5 7.75 8.25" />
      <path d="M12 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" />
    </svg>
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M1.5 8S3.9 3.75 8 3.75 14.5 8 14.5 8 12.1 12.25 8 12.25 1.5 8 1.5 8" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  );
}

export function EyeOffIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M6.35 4.05A6.6 6.6 0 0 1 8 3.85c4.1 0 6.5 4.15 6.5 4.15a12 12 0 0 1-2.2 2.66" />
      <path d="M10.15 9.9a2.4 2.4 0 0 1-3.3-3.3" />
      <path d="M4.35 5.2A11.7 11.7 0 0 0 1.5 8s2.4 4.15 6.5 4.15a6.7 6.7 0 0 0 2.35-.42" />
      <path d="m2.5 2.5 11 11" />
    </svg>
  );
}

/* ---- the eight nav marks ------------------------------------------- */

export function HomeIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M2.5 7 8 2.5 13.5 7" />
      <path d="M4 6.6v6.15a.75.75 0 0 0 .75.75h6.5a.75.75 0 0 0 .75-.75V6.6" />
      <path d="M6.6 13.5V9.75h2.8v3.75" />
    </svg>
  );
}

export function OrdersIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M2.5 5.25 8 2.5l5.5 2.75v5.5L8 13.5l-5.5-2.75z" />
      <path d="M2.5 5.25 8 8l5.5-2.75" />
      <path d="M8 8v5.5" />
    </svg>
  );
}

export function StockIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <rect x="2.5" y="2.75" width="4.5" height="4.5" rx="0.75" />
      <rect x="9" y="2.75" width="4.5" height="4.5" rx="0.75" />
      <rect x="2.5" y="8.75" width="4.5" height="4.5" rx="0.75" />
      <rect x="9" y="8.75" width="4.5" height="4.5" rx="0.75" />
    </svg>
  );
}

export function CustomersIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle cx="6.25" cy="5.75" r="2.5" />
      <path d="M1.75 13.25a4.5 4.5 0 0 1 9 0" />
      <path d="M10.5 3.6a2.5 2.5 0 0 1 0 4.3" />
      <path d="M11.75 9.75a4.5 4.5 0 0 1 2.5 3.5" />
    </svg>
  );
}

export function SupportIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M13.5 9.5a1.5 1.5 0 0 1-1.5 1.5H5.5L2.5 13.5V4a1.5 1.5 0 0 1 1.5-1.5h8a1.5 1.5 0 0 1 1.5 1.5z" />
      <path d="M6.5 6.25a1.5 1.5 0 1 1 1.9 1.45c-.35.1-.4.4-.4.8" />
      <path d="M8 9.9h.01" />
    </svg>
  );
}

export function FinanceIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M8 4.75v6.5" />
      <path d="M9.9 6.2a2 2 0 0 0-1.9-.95c-1.1 0-1.9.6-1.9 1.45S6.9 8 8 8s1.9.45 1.9 1.3-.8 1.45-1.9 1.45a2 2 0 0 1-1.9-.95" />
    </svg>
  );
}

export function GrowthIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M2.5 13.5V2.75" />
      <path d="M2.5 13.5h11" />
      <path d="m5 10.25 2.5-3 2 2 3.25-4" />
      <path d="M10.5 5.25h2.25V7.5" />
    </svg>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1.75 9 3.4l1.85-.35.6 1.8 1.8.6L12.9 7.3l1.35 1.35-1.35 1.35.35 1.85-1.8.6-.6 1.8L8 13.9l-1.85.35-.6-1.8-1.8-.6.35-1.85L2.75 8.65 4.1 7.3l-.35-1.85 1.8-.6.6-1.8z" />
    </svg>
  );
}

/** A label printer: a body, a slot, and the label coming out of it. */
export function BenchIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <rect x="2" y="5.5" width="12" height="6" rx="1" />
      <path d="M5 5.5V2.75h6V5.5" />
      <path d="M5 11.5v2.25h6V11.5" />
      <path d="M5 8.5h6" />
    </svg>
  );
}

const BY_NAME: Readonly<Record<IconName, (props: IconProps) => ReactElement>> = {
  chevron: ChevronIcon,
  search: SearchIcon,
  download: DownloadIcon,
  star: StarIcon,
  edit: EditIcon,
  warning: WarningIcon,
  external: ExternalIcon,
  eye: EyeIcon,
  eyeOff: EyeOffIcon,
  home: HomeIcon,
  orders: OrdersIcon,
  stock: StockIcon,
  customers: CustomersIcon,
  support: SupportIcon,
  finance: FinanceIcon,
  growth: GrowthIcon,
  settings: SettingsIcon,
  bench: BenchIcon,
};

export interface IconByNameProps extends IconProps {
  name: string;
}

/**
 * Lookup by name, for the nav tables that live in a `.ts` file and cannot hold JSX. An
 * unknown name renders nothing rather than throwing, because a missing glyph is a cosmetic
 * defect and the label beside it still carries the meaning.
 */
export function Icon({ name, ...rest }: IconByNameProps) {
  const Glyph = BY_NAME[name as IconName];
  return Glyph ? <Glyph {...rest} /> : null;
}
